import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BASE_URL = 'https://sampaolicreazioni.it';
const SITE_NAME = 'Sampaoli Creazioni';

interface PageSeoHeadProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  imageAlt?: string;
  type?: string;
  noindex?: boolean;
}

/**
 * Reusable SEO head component for non-product pages.
 * Sets document title, meta description, canonical, OG/Twitter tags,
 * hreflang alternates (IT/EN) and locale metadata.
 * Cleans up on unmount.
 */
export function PageSeoHead({
  title,
  description,
  path,
  image,
  imageAlt,
  type = 'website',
  noindex = false,
}: PageSeoHeadProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'it';

  useEffect(() => {
    const prev = document.title;
    document.title = title;

    const canonicalUrl = `${BASE_URL}${path}`;
    const ogImage = image || `${BASE_URL}/hero-mountains-clouds.jpg`;
    const ogLocale = lang === 'it' ? 'it_IT' : 'en_US';
    const ogLocaleAlt = lang === 'it' ? 'en_US' : 'it_IT';

    document.documentElement.setAttribute('lang', lang);

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[(.+?)="(.+?)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const setLink = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.href = href;
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', noindex ? 'noindex, nofollow' : 'index, follow');
    setLink('canonical', canonicalUrl);

    // hreflang alternates — site is bilingual (IT/EN) on the same URL via language toggle
    setLink('alternate', canonicalUrl, 'it');
    setLink('alternate', canonicalUrl, 'en');
    setLink('alternate', canonicalUrl, 'x-default');

    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[property="og:locale"]', 'content', ogLocale);
    setMeta('meta[property="og:locale:alternate"]', 'content', ogLocaleAlt);

    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);
    if (imageAlt) {
      setMeta('meta[name="twitter:image:alt"]', 'content', imageAlt);
    }

    return () => {
      document.title = prev;
    };
  }, [title, description, path, image, imageAlt, type, noindex, lang]);

  return null;
}
